import React from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Textarea } from './ui/textarea';

const LeadForm = ({ onSubmit, onCancel, loading = false }) => {
  const { t } = useTranslation();
  
  // React Hook Form - NO re-renders on input changes!
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors }
  } = useForm({
    defaultValues: {
      contact_name: '',
      contact_email: '',
      contact_phone: '',
      contact_whatsapp: '',
      contact_address: '',
      contact_language: 'es',
      contact_tcpa_consent: false,
      product_type: '',
      source: '',
      campaign: '',
      zip_code: '',
      notes: ''
    }
  });

  // Get translated product types and sources
  const PRODUCT_TYPES = ['Auto', 'Casa', 'Comercial'];
  const LEAD_SOURCES = ['Web', 'WhatsApp', 'Meta Ads', 'Google Ads', 'Referral', 'Call'];

  const getTranslatedProductTypes = () => {
    return PRODUCT_TYPES.map(type => t(`leads.productTypeOptions.${type.toLowerCase()}`) || type);
  };

  const getTranslatedLeadSources = () => {
    return LEAD_SOURCES.map(source => t(`leads.sourceOptions.${source.toLowerCase().replace(' ', '')}`) || source);
  };

  // Transform form data for backend API
  const onFormSubmit = (data) => {
    const transformedData = {
      contact: {
        name: data.contact_name,
        email: data.contact_email,
        phone: data.contact_phone,
        whatsapp: data.contact_whatsapp,
        address: data.contact_address,
        language: data.contact_language,
        tcpa_consent: data.contact_tcpa_consent
      },
      product_type: data.product_type,
      source: data.source,
      campaign: data.campaign,
      zip_code: data.zip_code,
      notes: data.notes
    };
    onSubmit(transformedData);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-6">
      <Tabs defaultValue="contact" className="w-full">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="contact">{t('leads.contactInfo')}</TabsTrigger>
          <TabsTrigger value="lead">{t('leads.leadInfo')}</TabsTrigger>
        </TabsList>
        
        <TabsContent value="contact" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contact_name">{t('leads.fullName')}</Label>
              <input
                {...register('contact_name', { required: 'Name is required' })}
                id="contact_name"
                type="text"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder={t('leads.fullName')}
              />
              {errors.contact_name && (
                <span className="text-sm text-red-500">{errors.contact_name.message}</span>
              )}
            </div>
            <div>
              <Label htmlFor="contact_email">{t('leads.email')}</Label>
              <input
                {...register('contact_email', { 
                  required: 'Email is required',
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: 'Invalid email address'
                  }
                })}
                id="contact_email"
                type="email"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder={t('leads.email')}
              />
              {errors.contact_email && (
                <span className="text-sm text-red-500">{errors.contact_email.message}</span>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contact_phone">{t('leads.phone')}</Label>
              <input
                {...register('contact_phone')}
                id="contact_phone"
                type="tel"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder={t('leads.phone')}
              />
            </div>
            <div>
              <Label htmlFor="contact_whatsapp">{t('leads.whatsapp')}</Label>
              <input
                {...register('contact_whatsapp')}
                id="contact_whatsapp"
                type="tel"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder={t('leads.whatsapp')}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="contact_address">{t('leads.address')}</Label>
            <input
              {...register('contact_address')}
              id="contact_address"
              type="text"
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
              placeholder={t('leads.address')}
            />
          </div>

          <div className="flex items-center space-x-2">
            <Switch
              id="contact_tcpa_consent"
              checked={watch('contact_tcpa_consent')}
              onCheckedChange={(checked) => setValue('contact_tcpa_consent', checked)}
            />
            <Label htmlFor="contact_tcpa_consent">{t('leads.tcpaConsent')}</Label>
          </div>
        </TabsContent>

        <TabsContent value="lead" className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="product_type">{t('leads.productType')}</Label>
              <Select
                value={watch('product_type')}
                onValueChange={(value) => setValue('product_type', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('leads.productType')} />
                </SelectTrigger>
                <SelectContent>
                  {getTranslatedProductTypes().map((type, index) => (
                    <SelectItem key={PRODUCT_TYPES[index]} value={PRODUCT_TYPES[index]}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="source">{t('leads.source')}</Label>
              <Select
                value={watch('source')}
                onValueChange={(value) => setValue('source', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('leads.source')} />
                </SelectTrigger>
                <SelectContent>
                  {getTranslatedLeadSources().map((source, index) => (
                    <SelectItem key={LEAD_SOURCES[index]} value={LEAD_SOURCES[index]}>
                      {source}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="campaign">{t('leads.campaign')}</Label>
              <input
                {...register('campaign')}
                id="campaign"
                type="text"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder={t('leads.campaign')}
              />
            </div>
            <div>
              <Label htmlFor="zip_code">{t('leads.zipCode')}</Label>
              <input
                {...register('zip_code')}
                id="zip_code"
                type="text"
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                placeholder={t('leads.zipCode')}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="notes">{t('leads.notes')}</Label>
            <Textarea
              {...register('notes')}
              id="notes"
              rows={4}
              className="resize-none"
              placeholder={t('leads.notes')}
            />
          </div>
        </TabsContent>
      </Tabs>

      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" disabled={loading} className="bg-mg-green hover:bg-mg-green/90">
          {loading ? t('leads.creating') : t('leads.createLead')}
        </Button>
      </div>
    </form>
  );
};

export default LeadForm;